/**
 * DemoBanner — persistent strip across the top of every page while the user
 * is in the shared Waypoint demo workspace. Click "Exit" to leave.
 *
 * Lives below ImpersonationBanner so the impersonation badge always wins
 * (impersonation is the more dangerous mode). Both can be active in theory,
 * but in practice impersonation only makes sense inside the real account.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { isDemoActive, setDemoAccountId } from '@/lib/utils/demo-mode';

export function DemoBanner() {
  const queryClient = useQueryClient();
  const [active, setActive] = useState<boolean>(() => isDemoActive());

  useEffect(() => {
    const onChange = () => setActive(isDemoActive());
    window.addEventListener('waypoint:demo-mode-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('waypoint:demo-mode-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const exit = useMutation({
    mutationFn: () => api<{ ok: boolean }>('/v1/demo/exit', { method: 'POST' }),
    onSuccess: () => {
      setDemoAccountId(null);
      queryClient.clear();
    },
  });

  if (!active) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-b border-warn/40 bg-warn/10 px-4 py-2 text-2xs text-warn">
      <div className="flex items-center gap-2 min-w-0">
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
          <path d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285zm0 13.036h.008v.008H12v-.008z" />
        </svg>
        <span className="font-semibold truncate">
          Demo workspace active — viewing seeded sample data, not your real account.
        </span>
      </div>
      <button
        onClick={() => exit.mutate()}
        disabled={exit.isPending}
        className="rounded-md border border-warn/40 bg-warn/10 px-2.5 py-1 text-2xs font-semibold hover:bg-warn/20 disabled:opacity-50 shrink-0"
      >
        {exit.isPending ? 'Exiting…' : 'Exit demo'}
      </button>
    </div>
  );
}
